import { TextProps } from '@features/powerpoint';
import { Subdimension } from '@features/reports/subdomains/visitation';
import { VisitMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { contentSlideTitleText } from '../../../../slides/content-slide-title-text';
import { dimensionMetrics, overallPerformanceMetrics } from './content';
import { theme } from '@features/executive-report/domain/theme';
import { ImageWithMetadata } from '@features/powerpoint';
import {
  applyDimensionXOffset,
  applyDimensionMetricYOffset,
  applyDimensionYOffset,
  applyOverallPerformanceYOffset,
} from '@features/executive-report/dimensions/layout';
import {
  recommendationText,
  blueDimensionBoxText,
  overallPerformanceText,
} from '@features/executive-report/dimensions/texts';
import { PerformanceMetric } from '@features/executive-report/dimensions/content';
import { headerTabs, TabId } from '../header-tabs';

const overallLineItemTexts = (
  metric: PerformanceMetric,
  index: number
): TextProps[] => [
  {
    text: metric.label.toUpperCase(),
    options: {
      fontSize: 9,
      color: theme.colors.white,
      h: 0.25,
      w: 2.71,
      x: 0.92,
      y: 3.57 + applyOverallPerformanceYOffset(index),
    },
  },
  {
    text: metric.value,
    options: {
      fontSize: 20,
      color: theme.colors.white,
      bold: true,
      h: 0.5,
      w: 2,
      x: 0.92,
      y: 3.75 + applyOverallPerformanceYOffset(index),
    },
  },
  ...(metric.lift
    ? [
        {
          text: metric.lift,
          options: {
            fontSize: 24,
            color: theme.colors.inmarketGreen,
            h: 0.5,
            w: 1.65,
            x: 2.3,
            y: 3.75 + applyOverallPerformanceYOffset(index),
          },
        },
      ]
    : []),
];

export type TextsOptions = {
  activeHeaderTab: TabId;
  recommendation: string;
  dimensionName: string;
  subdimensions: (Pick<Subdimension, 'value' | 'visitMetrics'> & {
    image: ImageWithMetadata | null;
  })[];
  overallMetrics: VisitMetrics;
};

export const texts = (options: TextsOptions): TextProps[] => [
  ...headerTabs(options.activeHeaderTab),
  contentSlideTitleText(
    `<b>The</b> <lci>${options.activeHeaderTab.toUpperCase()}:</lci> <b>${[
      options.dimensionName[0].toUpperCase(),
      options.dimensionName.slice(1),
      's',
    ].join('')}</b>`
  ),
  recommendationText(options.recommendation),
  overallPerformanceText,
  ...overallPerformanceMetrics(options.overallMetrics).flatMap(
    (metric, index): TextProps[] => overallLineItemTexts(metric, index)
  ),
  blueDimensionBoxText(options.dimensionName, options.subdimensions.length),
  ...options.subdimensions.flatMap((subdimension, index): TextProps[] => [
    {
      text: subdimension.value,
      options: {
        fontSize: 11,
        color: theme.colors.white,
        h: 0.29,
        w: 2.27,
        x: 4.35 + applyDimensionXOffset(index),
        y: 3.02 + applyDimensionYOffset(index),
      },
    },
    ...(subdimension.image
      ? []
      : [
          {
            text: (index + 1).toString(),
            options: {
              fontSize: 32,
              color: theme.colors.inmarketGreen,
              h: 0.64,
              w: 0.36,
              x: 4.83 + applyDimensionXOffset(index),
              y: 3.54 + applyDimensionYOffset(index),
            },
          },
          {
            text: options.dimensionName.toUpperCase(),
            options: {
              fontSize: 12,
              color: theme.colors.white,
              bold: true,
              h: 0.28,
              w: 1.21,
              x: 5.19 + applyDimensionXOffset(index),
              y: 3.74 + applyDimensionYOffset(index),
            },
          },
        ]),
    ...dimensionMetrics(subdimension.visitMetrics).flatMap(
      (metric, metricIndex): TextProps[] => [
        {
          text: metric.label.toUpperCase(),
          options: {
            fontSize: 8,
            color: theme.colors.white,
            h: 0.24,
            w: 1.46,
            x: 4.28 + applyDimensionXOffset(index),
            y:
              4.52 +
              applyDimensionYOffset(index) +
              applyDimensionMetricYOffset(metricIndex),
          },
        },
        {
          text: metric.value,
          options: {
            fontSize: 12,
            color: theme.colors.inmarketGreen,
            h: 0.3,
            w: 1.51,
            x: 5.6 + applyDimensionXOffset(index),
            y:
              4.49 +
              applyDimensionYOffset(index) +
              applyDimensionMetricYOffset(metricIndex),
          },
        },
      ]
    ),
  ]),
];
